import { RouterProvider } from '@tanstack/react-router';
import { useEffect } from 'react';
import { onSignedInUserChange } from '../../features/auth/authenticator';
import type { AppRouter } from '../router';
import { AppErrorBoundary } from './AppErrorBoundary';
import { AppProviders, type AppProvidersProps } from './AppProviders';
import { QueryDevtools } from './QueryDevtools';
import { RouterDevtools } from './RouterDevtools';

interface Props extends Omit<AppProvidersProps, 'children'> {
  readonly router: AppRouter;
  /** Locally and on the dev stage (`showsDeveloperTools`). */
  readonly showDevtools: boolean;
}

export function App({ router, showDevtools, ...providers }: Props) {
  const { authenticator, queryClient } = providers;

  // Signing in or out here or in another tab, or a session Firebase ends, re-runs the
  // route guards, and drops cached data so nobody sees the previous user's.
  useEffect(
    () =>
      onSignedInUserChange(authenticator, () => {
        void queryClient.resetQueries();
        void router.invalidate();
      }),
    [authenticator, queryClient, router],
  );

  return (
    <AppProviders {...providers}>
      <AppErrorBoundary>
        <RouterProvider router={router} />
        {showDevtools && (
          <>
            <RouterDevtools router={router} />
            <QueryDevtools />
          </>
        )}
      </AppErrorBoundary>
    </AppProviders>
  );
}
